import React from 'react';
import {
  Box,
  Avatar,
  Typography,
  Chip,
  ListItemButton,
} from '@mui/material';
import {
  PriorityHigh as UrgentIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';

const getPriorityConfig = (priority, isDraft) => {
  if (isDraft) {
    return { color: '#f59e0b', bgcolor: '#fef3c7', label: 'Draft' };
  }
  if (priority === 'Urgent') {
    return { color: '#dc2626', bgcolor: '#fef2f2', label: 'Urgent' };
  }
  return { color: '#6b7280', bgcolor: '#f3f4f6', label: 'Normal' };
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
};

const MessageListItem = ({ message, selected, onClick }) => {
  const priorityConfig = getPriorityConfig(message.priority, message.isDraft);
  
  return (
    <ListItemButton
      onClick={onClick}
      selected={selected}
      sx={{
        py: 1.5,
        px: 2,
        mx: 1,
        my: 0.5,
        borderRadius: 2,
        transition: 'all 0.15s ease-in-out',
        borderLeft: message.isDraft ? '3px solid #f59e0b' : '3px solid transparent',
        bgcolor: selected ? 'primary.50' : 'transparent',
        '&:hover': {
          bgcolor: selected ? 'primary.50' : 'action.hover',
          transform: 'translateX(2px)',
        },
        '&.Mui-selected': {
          bgcolor: 'primary.50',
          '&:hover': {
            bgcolor: 'primary.100',
          },
        },
      }}
    >
      {/* Avatar */}
      <Avatar
        sx={{
          width: 44,
          height: 44,
          mr: 2,
          bgcolor: message.isDraft ? '#fef3c7' : 'primary.100',
          color: message.isDraft ? '#92400e' : 'primary.700',
          fontWeight: 600,
          fontSize: '0.95rem',
        }}
      >
        {message.avatar ? (
          <img src={message.avatar} alt={message.from} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          getInitials(message.from)
        )}
      </Avatar>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
        {/* Top row: Name + Priority Badge */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: message.unread ? 700 : 600,
              color: message.isDraft ? '#92400e' : 'text.primary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
              fontSize: '0.9rem',
            }}
          >
            {message.from}
          </Typography>
          {message.unread && (
            <DotIcon sx={{ fontSize: 10, color: 'primary.main' }} />
          )}
        </Box>

        {/* Subject */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: message.unread ? 600 : 500,
            color: message.isDraft ? '#92400e' : 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '0.85rem',
            mb: 0.25,
          }}
        >
          {message.subject || '(No subject)'}
        </Typography>

        {/* Preview */}
        <Typography
          variant="body2"
          sx={{
            color: message.isDraft ? '#d97706' : 'text.secondary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '0.8rem',
          }}
        >
          {message.preview || 'No preview available'}
        </Typography>
      </Box>

      {/* Right side: Time + Priority */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'flex-end',
        gap: 0.5,
        minWidth: 70,
      }}>
        <Typography
          variant="caption"
          sx={{
            color: message.isDraft ? '#d97706' : 'text.disabled',
            fontSize: '0.75rem',
            fontWeight: 500,
          }}
        >
          {message.timestamp || ''}
        </Typography>
        
        <Chip
          size="small"
          label={priorityConfig.label}
          icon={message.priority === 'Urgent' ? <UrgentIcon sx={{ fontSize: '0.75rem !important' }} /> : undefined}
          sx={{
            height: 20,
            fontSize: '0.65rem',
            fontWeight: 600,
            bgcolor: priorityConfig.bgcolor,
            color: priorityConfig.color,
            '& .MuiChip-label': {
              px: 1,
            },
            '& .MuiChip-icon': {
              color: priorityConfig.color,
              ml: 0.5,
            },
          }}
        />
      </Box>
    </ListItemButton>
  );
};

export default MessageListItem;
